import _ from 'underscore';
import React, { useState, memo, useContext, useCallback, useRef } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import type {
  CacheContextValue,
  CacheKey,
  CacheMerge,
  CacheUpdate,
  ScopedCache,
} from '../types';

export const CacheContext = React.createContext<CacheContextValue>({
  cache: {},
});

export const useCache = <T extends unknown>(key: CacheKey): ScopedCache<T> => {
  const {
    cache,
    invalidate,
    doUpdateAndMerge,
    doFireAndForgetUpdate,
    tryUpdate,
  } = useContext(CacheContext);

  if (!invalidate || !doUpdateAndMerge || !doFireAndForgetUpdate || !tryUpdate) {
    throw new Error('CacheContext not set. Wrap component with CacheContext provider (withCacheProvider)');
  }

  const scopedCache = cache[key] as T | undefined;

  const scopedTryUpdate = useCallback(
    _.partial(tryUpdate, key) as (
      updateFunc: CacheUpdate<T>
    ) => PromiseLike<T | null>,
    [tryUpdate, key]
  );

  const scopedDoUpdateAndMerge = useCallback(
    _.partial(doUpdateAndMerge, key) as (
      updateFunc: CacheUpdate<T>,
      mergeFunc?: CacheMerge<T>
    ) => PromiseLike<T | null>,
    [doUpdateAndMerge, key]
  );

  const scopedDoFireAndForgetUpdate = useCallback(
    _.partial(doFireAndForgetUpdate, key) as (
      updateFunc: CacheUpdate<T>,
      mergeFunc?: CacheMerge<T>
    ) => PromiseLike<void>,
    [doFireAndForgetUpdate, key]
  );

  const scopedInvalidate = useCallback(
    _.partial(invalidate, key) as () => void,
    [invalidate, key]
  );

  return {
    cache: scopedCache,
    tryUpdate: scopedTryUpdate,
    doUpdateAndMerge: scopedDoUpdateAndMerge,
    doFireAndForgetUpdate: scopedDoFireAndForgetUpdate,
    invalidate: scopedInvalidate,
  };
};

export const withCacheProvider = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  const HoC = memo((props: P) => {
    const tokens = useRef<Record<CacheKey, PromiseLike<unknown>>>({}).current;
    const [cache, setCache] = useState<Record<CacheKey, unknown>>({});

    const doUpdate = useCallback(<T extends unknown>(
      key: CacheKey,
      updateFunc: CacheUpdate<T>,
      mergeFunc: CacheMerge<T> = _.identity
    ) => {
      if (!_.has(tokens, key)) {
        tokens[key] = updateFunc().then((payload = null) => {
          // if we fetch content, and get back nothing
          // set to null so consumers can verify the fetch finished
          setCache(prevCache => ({
            ...prevCache,
            [key]: mergeFunc(payload, prevCache[key] as T | undefined),
          }));
          delete tokens[key];

          // return raw payload to chain the results
          return payload;
        }, (error: unknown) => {
          delete tokens[key];
          throw error;
        });
      }
      return tokens[key] as PromiseLike<T | null>;
    }, [tokens]);

    const tryUpdate = useCallback(
      <T extends unknown>(key: CacheKey, updateFunc: CacheUpdate<T>) =>
        doUpdate(key, updateFunc),
      [doUpdate]
    );

    // Only used for actions where we don't need to track the result
    const doFireAndForgetUpdate = useCallback(
      <T extends unknown>(
        key: CacheKey,
        updateFunc: CacheUpdate<T>,
        mergeFunc: CacheMerge<T> = _.identity
      ) =>
        updateFunc().then((payload = null) => {
          // if we fetch content, and get back nothing
          // set to null so consumers can verify the fetch finished
          setCache(prevCache => ({
            ...prevCache,
            [key]: mergeFunc(payload, prevCache[key] as T | undefined),
          }));
        }),
      []
    );

    const doUpdateAndMerge = useCallback(<T extends unknown>(
      key: CacheKey,
      updateFunc: CacheUpdate<T>,
      mergeFunc: CacheMerge<T> = _.identity
    ) => {
      if (_.has(tokens, key)) {
        throw new Error(`Update already executing for ${key}`);
      }

      return doUpdate(key, updateFunc, mergeFunc);
    }, [doUpdate, tokens]);

    const invalidate = useCallback((key?: CacheKey) => {
      if (key) {
        setCache(prevCache => (_.omit(prevCache, key)));
      } else {
        setCache({});
      }
    }, []);

    return (
      <CacheContext.Provider value={{
        cache,
        tryUpdate,
        doUpdateAndMerge,
        doFireAndForgetUpdate,
        invalidate,
      }}
      >
        <WrappedComponent {...props} />
      </CacheContext.Provider>);
  });

  HoC.displayName = `withCacheProvider(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(HoC, WrappedComponent);
};
